import React, { useState, useEffect } from 'react';
import apiService from '../services/ApiService';
import { AvatarUpload } from './AvatarUpload';
import { CustomDatePicker } from './CustomDatePicker';
import CustomDropdown from './CustomDropdown';
import { useOnboarding } from '../hooks/useOnboarding';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProfileUpdated: (updatedProfile: any) => void;
  currentUser: any;
}

interface Country {
  code: string;
  name: string;
  continent: string;
  sort_order: number;
}

interface StateProvince {
  code: string;
  name: string;
  type: string;
}

export const EditProfileModal: React.FC<EditProfileModalProps> = ({
  isOpen,
  onClose,
  onProfileUpdated,
  currentUser
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    country: '',
    stateProvince: '',
    gender: '',
    birthDate: '',
    instagramUsername: '',
    facebookUrl: '',
    websiteUrl: '',
    bio: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [passwordErrors, setPasswordErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const onboarding = useOnboarding();
  const [onboardingBusy, setOnboardingBusy] = useState(false);
  const [onboardingMessage, setOnboardingMessage] = useState('');
  const [countries, setCountries] = useState<Country[]>([]);
  const [statesProvinces, setStatesProvinces] = useState<StateProvince[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarPosition, setAvatarPosition] = useState<{ x: number; y: number; scale: number } | null>(null);

  // Load profile data when modal opens
  useEffect(() => {
    if (isOpen && currentUser) {
      loadUserProfile();
      loadCountries();
    } else if (!isOpen) {
      // Clear password fields when modal closes for security
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordErrors({});
      setPasswordSuccess('');
    }
  }, [isOpen, currentUser]);

  // Load states/provinces when country changes
  useEffect(() => {
    if (formData.country) {
      loadStatesProvinces(formData.country);
    } else {
      setStatesProvinces([]);
      setFormData(prev => ({ ...prev, stateProvince: '' }));
    }
  }, [formData.country]);

  const loadUserProfile = async () => {
    try {
      const profile = await apiService.getUserProfile();
      setFormData({
        firstName: profile.first_name || '',
        lastName: profile.last_name || '',
        country: profile.country || '',
        stateProvince: profile.state_province || '',
        gender: profile.gender || '',
        birthDate: profile.birth_date || '',
        instagramUsername: profile.instagram_username || '',
        facebookUrl: profile.facebook_url || '',
        websiteUrl: profile.website_url || '',
        bio: profile.bio || ''
      });
      setAvatarUrl(profile.avatar_url || null);
      setAvatarPosition(profile.avatar_position || null);
    } catch (error) {
      console.error('Failed to load user profile:', error);
      setGeneralError('Failed to load profile data');
    }
  };

  const loadCountries = async () => {
    try {
      const countriesData = await apiService.getCountries();
      setCountries(countriesData);
    } catch (error) {
      console.error('Failed to load countries:', error);
    }
  };

  const loadStatesProvinces = async (countryCode: string) => {
    setLoadingStates(true);
    try {
      const statesData = await apiService.getStatesProvinces(countryCode);
      setStatesProvinces(statesData);
    } catch (error) {
      console.error('Failed to load states/provinces:', error);
      setStatesProvinces([]);
    } finally {
      setLoadingStates(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Country validation (required)
    if (!formData.country) {
      newErrors.country = 'Country is required';
    }

    // State/Province validation (required)
    if (!formData.stateProvince) {
      newErrors.stateProvince = 'State/Province is required';
    }

    // Gender validation (required)
    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    // Birth date validation (required)
    if (!formData.birthDate) {
      newErrors.birthDate = 'Birth date is required';
    } else {
      const today = new Date();
      const birthDate = new Date(formData.birthDate);
      const age = today.getFullYear() - birthDate.getFullYear();
      
      if (birthDate > today) {
        newErrors.birthDate = 'Birth date cannot be in the future';
      } else if (age > 120) {
        newErrors.birthDate = 'Please enter a valid birth date';
      } else if (age < 13) {
        newErrors.birthDate = 'You must be at least 13 years old';
      }
    }

    // Instagram username validation
    if (formData.instagramUsername && !/^[a-zA-Z0-9_.]+$/.test(formData.instagramUsername)) {
      newErrors.instagramUsername = 'Instagram username can only contain letters, numbers, dots, and underscores';
    }

    // URL validations
    if (formData.facebookUrl && !isValidUrl(formData.facebookUrl)) {
      newErrors.facebookUrl = 'Please enter a valid Facebook URL';
    }

    if (formData.websiteUrl && !isValidUrl(formData.websiteUrl)) {
      newErrors.websiteUrl = 'Please enter a valid website URL';
    }

    // Bio validation
    if (formData.bio && formData.bio.length > 500) {
      newErrors.bio = 'Bio must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setGeneralError('');

    try {
      const updatedProfile = await apiService.updateUserProfile({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        country: formData.country,
        stateProvince: formData.stateProvince,
        gender: formData.gender,
        birthDate: formData.birthDate,
        instagramUsername: formData.instagramUsername || undefined,
        facebookUrl: formData.facebookUrl || undefined,
        websiteUrl: formData.websiteUrl || undefined,
        bio: formData.bio || undefined,
      });

      onProfileUpdated(updatedProfile);
      onClose();

    } catch (error: any) {
      console.error('Profile update failed:', error);
      setGeneralError(error.message || 'Profile update failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({ ...prev, [field]: '' }));
    }
    // Clear success message when user starts typing
    if (passwordSuccess) {
      setPasswordSuccess('');
    }
  };

  const handleAvatarUpdate = (newAvatarUrl: string | null, position?: { x: number; y: number; scale: number }) => {
    setAvatarUrl(newAvatarUrl);
    setAvatarPosition(position || null);
    // Notify parent component of avatar change
    if (onProfileUpdated) {
      onProfileUpdated({
        ...currentUser,
        avatar_url: newAvatarUrl,
        avatar_position: position
      });
    }
  };

  const handleAvatarError = (error: string) => {
    setGeneralError(error);
  };

  const validatePasswordForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!passwordData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters long';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordData.newPassword)) {
      newErrors.newPassword = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }

    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePasswordForm()) {
      return;
    }

    setIsPasswordLoading(true);
    setPasswordSuccess('');

    try {
      await apiService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setPasswordSuccess('Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

    } catch (error: any) {
      console.error('Password change failed:', error);
      setPasswordErrors({ general: error.message || 'Password change failed. Please try again.' });
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'non_binary', label: 'Non-binary' },
    { value: 'other', label: 'Other' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' }
  ];

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="modal-overlay"
      onClick={handleBackdropClick}
    >
      <div className="modal-panel max-w-2xl max-h-[90vh] overflow-y-auto p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="heading-section">Edit Profile</h2>
            <button
              onClick={onClose}
              className="btn-icon"
              disabled={isLoading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* General Error */}
          {generalError && (
            <div className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm">
              {generalError}
            </div>
          )}

          {/* Avatar Upload Section */}
          <div className="mb-8">
            <AvatarUpload
              currentAvatarUrl={avatarUrl || undefined}
              currentPosition={avatarPosition || undefined}
              onAvatarUpdate={handleAvatarUpdate}
              onError={handleAvatarError}
            />
          </div>

          {/* Edit Profile Form */}
          <div className="space-y-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Information */}
              <div className="border-b border-white/[0.08] pb-6">
                <h3 className="text-lg font-medium text-white mb-4">Basic Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Username (Read-only) */}
                <div className="md:col-span-2">
                  <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    id="username"
                    value={currentUser?.username || ''}
                    className="w-full px-3 py-2 bg-[#1a1a1a] border border-white/[0.14] rounded-lg text-gray-300 cursor-not-allowed"
                    disabled
                    readOnly
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    Username cannot be changed
                  </p>
                </div>

                {/* First Name */}
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={`input ${errors.firstName ? 'border-red-500' : ''}`}
                    placeholder="Your first name"
                    disabled={isLoading}
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-400">{errors.firstName}</p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={`input ${errors.lastName ? 'border-red-500' : ''}`}
                    placeholder="Your last name"
                    disabled={isLoading}
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-400">{errors.lastName}</p>
                  )}
                </div>

                {/* Country */}
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-300 mb-1">
                    Country *
                  </label>
                  <CustomDropdown
                    value={formData.country}
                    onChange={(value) => handleInputChange('country', value)}
                    options={countries.map((country) => ({
                      value: country.code,
                      label: country.name
                    }))}
                    placeholder="Select a country"
                    disabled={isLoading}
                    error={!!errors.country}
                  />
                  {errors.country && (
                    <p className="mt-1 text-sm text-red-400">{errors.country}</p>
                  )}
                </div>

                {/* State/Province */}
                <div>
                  <label htmlFor="stateProvince" className="block text-sm font-medium text-gray-300 mb-1">
                    State/Province *
                  </label>
                  <CustomDropdown
                    value={formData.stateProvince}
                    onChange={(value) => handleInputChange('stateProvince', value)}
                    options={[
                      { 
                        value: '', 
                        label: loadingStates ? 'Loading...' : formData.country ? 'Select state/province' : 'Select country first'
                      },
                      ...statesProvinces.map((state) => ({
                        value: state.name,
                        label: state.name
                      }))
                    ]}
                    placeholder={loadingStates ? 'Loading...' : formData.country ? 'Select state/province' : 'Select country first'}
                    disabled={isLoading || !formData.country || loadingStates}
                    error={!!errors.stateProvince}
                  />
                  {errors.stateProvince && (
                    <p className="mt-1 text-sm text-red-400">{errors.stateProvince}</p>
                  )}
                </div>

                {/* Gender */}
                <div>
                  <label htmlFor="gender" className="block text-sm font-medium text-gray-300 mb-1">
                    Gender *
                  </label>
                  <CustomDropdown
                    value={formData.gender}
                    onChange={(value) => handleInputChange('gender', value)}
                    options={genderOptions}
                    placeholder="Select gender"
                    disabled={isLoading}
                    error={!!errors.gender}
                  />
                  {errors.gender && (
                    <p className="mt-1 text-sm text-red-400">{errors.gender}</p>
                  )}
                </div>

                {/* Birth Date */}
                <div>
                  <CustomDatePicker
                    label="Birth Date *"
                    value={formData.birthDate}
                    onChange={(date) => handleInputChange('birthDate', date)}
                    placeholder="Select your birth date..."
                    disabled={isLoading}
                    className={errors.birthDate ? 'border-red-500' : ''}
                  />
                  {errors.birthDate && (
                    <p className="mt-1 text-sm text-red-400">{errors.birthDate}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="pb-6">
              <h3 className="text-lg font-medium text-white mb-4">Additional Information (Optional)</h3>
              
              {/* Bio */}
              <div className="mb-4">
                <label htmlFor="bio" className="block text-sm font-medium text-gray-300 mb-1">
                  Bio
                </label>
                <textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  className={`input resize-none ${errors.bio ? 'border-red-500' : ''}`}
                  placeholder="Tell us about yourself and your car passion..."
                  disabled={isLoading}
                  rows={3}
                  maxLength={500}
                />
                <div className="flex justify-between mt-1">
                  {errors.bio && (
                    <p className="text-sm text-red-400">{errors.bio}</p>
                  )}
                  <p className="text-xs text-gray-400 ml-auto">
                    {formData.bio.length}/500 characters
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Instagram */}
                <div>
                  <label htmlFor="instagramUsername" className="block text-sm font-medium text-gray-300 mb-1">
                    Instagram Username
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-400">@</span>
                    <input
                      type="text"
                      id="instagramUsername"
                      value={formData.instagramUsername}
                      onChange={(e) => handleInputChange('instagramUsername', e.target.value)}
                      className={`input pl-8 ${errors.instagramUsername ? 'border-red-500' : ''}`}
                      placeholder="your_username"
                      disabled={isLoading}
                    />
                  </div>
                  {errors.instagramUsername && (
                    <p className="mt-1 text-sm text-red-400">{errors.instagramUsername}</p>
                  )}
                </div>

                {/* Facebook */}
                <div>
                  <label htmlFor="facebookUrl" className="block text-sm font-medium text-gray-300 mb-1">
                    Facebook URL
                  </label>
                  <input
                    type="url"
                    id="facebookUrl"
                    value={formData.facebookUrl}
                    onChange={(e) => handleInputChange('facebookUrl', e.target.value)}
                    className={`input ${errors.facebookUrl ? 'border-red-500' : ''}`}
                    placeholder="https://facebook.com/yourprofile"
                    disabled={isLoading}
                  />
                  {errors.facebookUrl && (
                    <p className="mt-1 text-sm text-red-400">{errors.facebookUrl}</p>
                  )}
                </div>

                {/* Website */}
                <div className="md:col-span-2">
                  <label htmlFor="websiteUrl" className="block text-sm font-medium text-gray-300 mb-1">
                    Website URL
                  </label>
                  <input
                    type="url"
                    id="websiteUrl"
                    value={formData.websiteUrl}
                    onChange={(e) => handleInputChange('websiteUrl', e.target.value)}
                    className={`input ${errors.websiteUrl ? 'border-red-500' : ''}`}
                    placeholder="https://yourwebsite.com"
                    disabled={isLoading}
                  />
                  {errors.websiteUrl && (
                    <p className="mt-1 text-sm text-red-400">{errors.websiteUrl}</p>
                  )}
                </div>
              </div>
            </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-white/[0.08]">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary disabled:opacity-50 flex items-center space-x-2"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </form>

            {/* Password Change Section */}
            <div className="border-t border-white/[0.08] pt-6">
              <h3 className="text-lg font-medium text-white mb-4">Change Password</h3>
              
              {/* Password Success Message */}
              {passwordSuccess && (
                <div className="mb-4 p-3 bg-green-900/50 border border-green-700 rounded-lg text-green-200 text-sm">
                  {passwordSuccess}
                </div>
              )}

              {/* Password General Error */}
              {passwordErrors.general && (
                <div className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm">
                  {passwordErrors.general}
                </div>
              )}

              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  {/* Current Password */}
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-300 mb-1">
                      Current Password *
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                      className={`input ${passwordErrors.currentPassword ? 'border-red-500' : ''}`}
                      placeholder="Enter your current password"
                      disabled={isPasswordLoading}
                    />
                    {passwordErrors.currentPassword && (
                      <p className="mt-1 text-sm text-red-400">{passwordErrors.currentPassword}</p>
                    )}
                  </div>

                  {/* New Password */}
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-300 mb-1">
                      New Password *
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      value={passwordData.newPassword}
                      onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                      className={`input ${passwordErrors.newPassword ? 'border-red-500' : ''}`}
                      placeholder="Enter your new password"
                      disabled={isPasswordLoading}
                    />
                    {passwordErrors.newPassword && (
                      <p className="mt-1 text-sm text-red-400">{passwordErrors.newPassword}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-400">
                      Password must be at least 8 characters with uppercase, lowercase, and number
                    </p>
                  </div>

                  {/* Confirm New Password */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-1">
                      Confirm New Password *
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                      className={`input ${passwordErrors.confirmPassword ? 'border-red-500' : ''}`}
                      placeholder="Confirm your new password"
                      disabled={isPasswordLoading}
                    />
                    {passwordErrors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-400">{passwordErrors.confirmPassword}</p>
                    )}
                  </div>
                </div>

                {/* Password Change Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isPasswordLoading}
                    className="btn-danger disabled:opacity-50 flex items-center space-x-2"
                  >
                    {isPasswordLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Changing Password...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Change Password
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            <div className="border-t border-white/[0.08] pt-6 mt-6">
              <h3 className="heading-card mb-2">Setup guide</h3>
              <p className="text-sm text-muted mb-4">
                Bring back the garage checklist or start fresh if you dismissed it earlier.
              </p>
              {onboardingMessage && (
                <p className="text-sm text-emerald-400 mb-3">{onboardingMessage}</p>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  disabled={onboardingBusy}
                  onClick={async () => {
                    setOnboardingBusy(true);
                    setOnboardingMessage('');
                    try {
                      await onboarding.showSetupGuide();
                      setOnboardingMessage('Setup guide restored. Open Home or Garage to continue.');
                    } finally {
                      setOnboardingBusy(false);
                    }
                  }}
                  className="min-h-[44px] px-4 btn-secondary text-sm"
                >
                  Show setup guide
                </button>
                <button
                  type="button"
                  disabled={onboardingBusy}
                  onClick={async () => {
                    setOnboardingBusy(true);
                    setOnboardingMessage('');
                    try {
                      await onboarding.resetOnboarding();
                      setOnboardingMessage('Onboarding reset.');
                    } finally {
                      setOnboardingBusy(false);
                    }
                  }}
                  className="btn-ghost min-h-[44px] text-sm"
                >
                  Reset onboarding
                </button>
              </div>
            </div>
          </div>
      </div>
    </div>
  );
}; 